import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

import { CarouselPromo } from 'src/domains/layouts/webview/components/homePageCarouselPromos/carouselPromo/CarouselPromo';
import { QuickLinkTrayContainer } from 'src/domains/layouts/webview/components/quickLinkTray/QuickLinkTray';
import { NextRacesHomePage } from 'src/domains/sportsbook/webview/components/nextRaces/NextRacesHomePage';
import { EventsInPlayOrUpcoming } from 'src/domains/sportsbook/webview/components/containers/eventsInPlayOrUpcoming/EventsInPlayOrUpcoming';

import { HeroWidget } from 'src/domains/layouts/webview/components/heroEvents/heroWidget/HeroWidget';
import { PromotedEventsWidget } from 'src/domains/sportsbook/webview/components/containers/promotedEvents/PromotedEventsWidget';
import { EventsStartingSoon } from 'src/domains/sportsbook/webview/components/containers/eventsStartingSoon/EventsStartingSoon';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { TopEvents } from 'src/domains/sportsbook/webview/components/containers/topEvents/TopEvents';
import { VirtualGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/virtuals/VirtualGamesHomepageLazyLoading';
import { CasinoGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/casino/CasinoGamesHomepageLazyLoading';
import { HomepageWidgetType } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';
import { XtremePushBanner } from '../../webview/components/xtremePush/xtremePushBanner/XtremePushBanner';
import { SliderDataState } from 'src/domains/sportsbook/webview/components/nextRaces/NextRaces.state';
import { LazyLoadWrapper } from 'src/domains/casino/utils/lazyLoadWrapper';

type WidgetEntry = {
    component: React.ReactNode;
    condition?: boolean;
    shouldLazyLoad?: boolean;
};

export const HomepageContent = observer('HomepageContent', () => {
    const common = useCommon();
    const appCasinoState = AppCasinoState.get(common);

    const config = ConfigComponents.get(common).unifiedComponentConfig;
    const { hasGamesOnHomepage, hiddenSportsList, startingSoonEvents, topEvents, xtremePush } = config;
    const isRacingHiddenForOperator =
        hiddenSportsList.includes('horseracing') && hiddenSportsList.includes('greyhoundracing');
    const isFirstHRLoadingCompleted = SliderDataState.get(common, 'horseracing').isFirstHRLoadingCompleted;
    const widgetMap = new Map<HomepageWidgetType, WidgetEntry>([
        ['promoCarousel', { component: <CarouselPromo modeType='homepage' /> }],
        ['quickLinkTray', { component: <QuickLinkTrayContainer /> }],
        ['racingWidget', { component: <NextRacesHomePage />, condition: isRacingHiddenForOperator === false }],
        ['heroWidget', { component: <HeroWidget /> }],
        ['xtremePush', { component: <XtremePushBanner type='sportsbook' />, condition: xtremePush === true }],
        [
            'topEvents',
            {
                component: <TopEvents />,
                condition: topEvents === true && isFirstHRLoadingCompleted === true,
                shouldLazyLoad: true,
            },
        ],
        [
            'inPlay',
            {
                component: <EventsInPlayOrUpcoming showPriceLegend={false} />,
                condition: isFirstHRLoadingCompleted === true,
                shouldLazyLoad: true,
            },
        ],
        [
            'startingSoon',
            {
                component: <EventsStartingSoon showPriceLegend={false} />,
                condition: startingSoonEvents === true && isFirstHRLoadingCompleted === true,
                shouldLazyLoad: true,
            },
        ],
        [
            'casinoGames',
            {
                component: <CasinoGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true && isFirstHRLoadingCompleted === true,
                shouldLazyLoad: true,
            },
        ],
        [
            'promotedEvents',
            {
                component: <PromotedEventsWidget />,
                condition: isFirstHRLoadingCompleted === true && topEvents === false,
                shouldLazyLoad: true,
            },
        ],
        [
            'virtualGames',
            {
                component: <VirtualGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true && isFirstHRLoadingCompleted === true,
                shouldLazyLoad: true,
            },
        ],
        ['priceChangeLegend', { component: <PriceChangeLegend isRace={false} />, shouldLazyLoad: true }],
        ['seoPagesContent', { component: <SeoPagesContent pageSlug='homepage' /> }],
    ]);

    const widgetsOrder = [...new Set(config.homepageWidgetsOrder)];

    return (
        <>
            {widgetsOrder.map((key) => {
                const widget = widgetMap.get(key);

                if (!widget || widget.condition === false) {
                    return null;
                }

                if (widget.shouldLazyLoad) {
                    return (
                        <LazyLoadWrapper
                            intersectionObserverOptions={{ rootMargin: '0px' }}
                            id={key}
                            key={key}
                        >
                            {widget.component}
                        </LazyLoadWrapper>
                    );
                }

                return <React.Fragment key={key}>{widget.component}</React.Fragment>;
            })}
        </>
    );
});
